import { Button, Icon, Link, ModalDialog, Select, Surface, Text } from '@lawpilots/design-system'
import { Box, Stack } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CompanyLogo, LoadingPanel, PageLayout } from '@/common/components'
import ImageToSvgConverter from '@/common/components/ImageToSvgConverter/ImageToSvgConverter'
import { ParticipantsImg } from '@/common/components/ParticipantsImg'
import { FEATURES } from '@/features.config'
import { useHistory, usePageVisited } from '@/hooks'
import i18n from '@/locales/i18n'
import { UI_LANGUAGE_TAGS, toUiLanguageTag, useUiLanguageLabel } from '@/locales/uiLanguages'
import { tenantApi } from '@/services/api/config/api'
import { useListMyCoursesQuery, useStartMyCourseMutation } from '@/services/api/tenant/learnerCoursesEnhanced'
import { useUpdateUserSettingsMutation } from '@/services/api/tenant/userEnhanced'
import { RootState, useTypedDispatch, useTypedSelector } from '@/store'
import { CourseLanguagePicker } from '../components/CourseLanguagePicker'

export type StartCourseProps = {
  courseCode: string
}

const MY_COURSES_PATH = '/my/courses'

// The radio group is named by the visible heading above it rather than repeating the copy.
const LANGUAGE_LABEL_ID = 'course-language-label'

const PANEL_MAX_WIDTH = '720px'

const ContentStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 3,
  padding: '48px 24px',
  margin: '0 auto',
  maxWidth: PANEL_MAX_WIDTH,
  textAlign: 'center',
}

const IllustrationStyles = { width: '100%', maxWidth: '463px' }

// The screen carries no app chrome, so it brands itself the way the dialog it replaces did: the
// tenant's own logo where one is set, the lawpilots mark otherwise, centred above the panel.
const BrandBarStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '24px 16px',
  '& svg': { maxHeight: '40px', width: 'auto' },
}

// The language block hugs its own content and is centred as a unit, rather than stretching to
// the panel and stranding the label out on the left edge. The label stays left-aligned against
// the first radio column.
const LanguageBlockStyles = { width: 'max-content', maxWidth: '100%', textAlign: 'left' }

// Two equal halves so the split between the buttons falls on the page's centre line: Back ends
// at the centre, Continue starts at it. Centring the pair as a group instead let the wider Back
// button straddle the centre. Below sm they stack and centre normally.
const ActionRowStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  justifyItems: 'center',
  columnGap: 2,
  rowGap: 1,
  width: '100%',
  // On top of the column's own gap, so the call to action is not crowded by the language list
  // above it.
  marginTop: 2,
  '& > :first-of-type': { justifySelf: { xs: 'center', sm: 'end' } },
  '& > :last-of-type': { justifySelf: { xs: 'center', sm: 'start' } },
}

// Everything that is not the task itself lives on one quiet rule-off line at the foot of the
// surface: the page-language switch on the left, the legal links on the right.
const PageFooterStyles = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  width: '100%',
  // The rule sits the same 16px from the buttons above it as its content does below it. The
  // negative margin trims the column's own 24px gap down to that 16px; padding cannot do it,
  // since the border is drawn at the box's top edge.
  marginTop: -1,
  paddingTop: 2,
  borderTop: '1px solid var(--colors-border-subtle)',
}

const InlineRowStyles = { display: 'flex', alignItems: 'center', gap: 1 }
const LegalLinksStyles = { display: 'flex', alignItems: 'center', gap: 2 }

const PageLanguageSelectStyles = { minWidth: '180px' }

/**
 * The screen between My courses and the course player: it names the course and takes the
 * course-language choice the launch needs.
 */
const StartCourse = ({ courseCode }: StartCourseProps): JSX.Element => {
  const { t } = useTranslation(['Lmsplus_Course_Intro_Page', 'general'])
  const history = useHistory()
  const tenant = useTypedSelector((state: RootState) => state.authSlice.tenant)
  const { data: courses, isLoading } = useListMyCoursesQuery({ tenantId: tenant.id })
  const user = useTypedSelector((state: RootState) => state.authSlice.user)
  const dispatch = useTypedDispatch()
  const [updateUserSettings] = useUpdateUserSettingsMutation()
  const [startMyCourse, { isLoading: isStarting }] = useStartMyCourseMutation()
  const languageLabel = useUiLanguageLabel()
  usePageVisited('Start Course')

  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null)
  const [pendingVariantId, setPendingVariantId] = useState<string | null>(null)
  const [startFailed, setStartFailed] = useState(false)
  const pageLanguage = toUiLanguageTag(i18n.language)

  const pageLanguageOptions = UI_LANGUAGE_TAGS.map((tag) => ({ value: tag, label: languageLabel(tag) }))

  const course = courses?.find((candidate) => candidate.course_code === courseCode)

  // A code that matches nothing the learner is enrolled in has no page to show - a hand-typed or
  // stale URL belongs back on the list rather than on an empty shell.
  const courseMissing = !isLoading && courses !== undefined && course === undefined
  useEffect(() => {
    if (courseMissing) history.replace(MY_COURSES_PATH)
  }, [courseMissing, history])

  const firstPlay = course?.progress === 'not_started'
  const resumeUrl = course?.courseplay_url

  const canChooseLanguage = firstPlay || course?.progress === 'in_progress'

  const currentVariantId = course?.course_variant_id ?? null
  const shownVariantId = selectedVariantId ?? currentVariantId
  const variantToStart = selectedVariantId && selectedVariantId !== currentVariantId ? selectedVariantId : null

  const chooseVariant = (variantId: string) => {
    if (firstPlay || variantId === currentVariantId) setSelectedVariantId(variantId)
    else setPendingVariantId(variantId)
  }

  const confirmVariantChange = () => {
    setSelectedVariantId(pendingVariantId)
    setPendingVariantId(null)
  }

  const changePageLanguage = async (value: string) => {
    if (value === pageLanguage) return

    try {
      // The user's own tenant: they have no user record under an accessed one.
      if (user?.user_id && user.tenant_id) {
        await updateUserSettings({
          tenantId: user.tenant_id,
          userId: user.user_id,
          updateUserSettingsDto: { language_tag: value },
        }).unwrap()
      }
      await i18n.changeLanguage(value)
      dispatch(tenantApi.util.resetApiState())
    } catch (error) {
      console.error('Failed to update language:', error)
    }
  }

  const continueToCourse = async () => {
    if (!course || !variantToStart) {
      if (resumeUrl) window.location.assign(resumeUrl)
      return
    }

    setStartFailed(false)
    try {
      // The accessed tenant: the course belongs to its catalogue, not the user's own tenant.
      const { courseplay_url } = await startMyCourse({
        tenantId: tenant.id,
        enrollmentId: course.enrollment_id,
        startLearnerCourseInDto: { course_variant_id: variantToStart },
      }).unwrap()
      window.location.assign(courseplay_url)
    } catch (error) {
      console.error('Failed to start the course:', error)
      setStartFailed(true)
    }
  }

  return (
    <PageLayout feature={FEATURES.MY_COURSES}>
      <Box sx={BrandBarStyles}>
        {tenant.logo_url ? <ImageToSvgConverter imageString={tenant.logo_url} /> : <CompanyLogo />}
      </Box>
      <LoadingPanel loading={isLoading}>
        {course && (
          <Surface>
            <Box sx={ContentStyles}>
              <Box sx={IllustrationStyles}>
                <ParticipantsImg />
              </Box>

              <Text variant="page-title" color="primary" as="h1">
                {firstPlay ? t('trainings_welcome') : t('continue_course')}
              </Text>

              <Text variant="body-reading" color="secondary" style={{ whiteSpace: 'pre-line' }}>
                {t('trainings_description')}
              </Text>

              {/* The design picks the course name out in the interactive blue; `TextColor` has no
                  interactive member, so the token is applied directly. */}
              <Text variant="section-title" style={{ color: 'var(--colors-text-interactive-default)' }}>
                {course.course_name}
              </Text>

              {canChooseLanguage && (
                <Box sx={LanguageBlockStyles}>
                  <Stack spacing={1}>
                    <Text variant="body-strong" color="primary" as="h2" id={LANGUAGE_LABEL_ID}>
                      {firstPlay ? t('trainings_courseSelect') : t('change_training_language', { ns: 'general' })}
                    </Text>
                    <CourseLanguagePicker
                      tenantId={tenant.id}
                      courseCode={courseCode}
                      labelledBy={LANGUAGE_LABEL_ID}
                      selectedVariantId={shownVariantId}
                      onSelect={chooseVariant}
                    />
                  </Stack>
                </Box>
              )}

              <Box sx={ActionRowStyles}>
                <Button
                  variant="secondary"
                  icon={<Icon name="arrow-left" />}
                  onClick={() => history.push(MY_COURSES_PATH)}>
                  {t('back_to_my_courses')}
                </Button>
                <Button
                  variant="primary"
                  icon={<Icon name="arrow-right" />}
                  iconPosition="right"
                  disabled={(firstPlay ? !shownVariantId : !resumeUrl && !variantToStart) || isStarting}
                  onClick={continueToCourse}>
                  {t('trainings_continueButton')}
                </Button>
              </Box>

              {startFailed && (
                <Text variant="body-reading" color="secondary" role="alert">
                  {t('course_start_unavailable')}
                </Text>
              )}

              <Box sx={PageFooterStyles}>
                <Box sx={InlineRowStyles}>
                  <Text variant="secondary-compact" color="secondary" as="span">
                    {t('show_this_page')}
                  </Text>
                  <Box sx={PageLanguageSelectStyles}>
                    <Select
                      aria-label={t('show_this_page')}
                      // Never actually shown - the page always has a language - but the prop is required.
                      placeholder={t('please_select_a_language')}
                      options={pageLanguageOptions}
                      value={pageLanguage}
                      onChange={changePageLanguage}
                    />
                  </Box>
                </Box>

                <Box sx={LegalLinksStyles}>
                  <Link variant="secondary" href="https://lawpilots.com/de/datenschutzerklaerung/">
                    {t('data_privacy')}
                  </Link>
                  <Text variant="secondary-compact" color="subtle" as="span" aria-hidden="true">
                    ·
                  </Text>
                  <Link variant="secondary" href="https://lawpilots.com/de/impressum/">
                    {t('imprint')}
                  </Link>
                </Box>
              </Box>
            </Box>
          </Surface>
        )}
      </LoadingPanel>

      <ModalDialog
        open={pendingVariantId !== null}
        onClose={() => setPendingVariantId(null)}
        title={t('change_training_language', { ns: 'general' })}
        description={`${t('change_training_language_first_paragraph', { ns: 'general' })} ${t(
          'change_training_language_second_paragraph',
          { ns: 'general' },
        )}`}
        confirm={{ label: t('change_training_language', { ns: 'general' }), onClick: confirmVariantChange }}
        cancelLabel={t('trainings_cancel', { ns: 'general' })}
      />
    </PageLayout>
  )
}

export default StartCourse
